import { useCallback, useRef, useState } from 'react';
import { SimpleCache } from '../../utils/simple-cache';

import type {
	PreQuerySupplierArgs,
	ResultSuppliers,
} from '../product-router/product/result-provider/result-provider-types';
import { type SearchItems } from '../product-router/product/result-types';
import {
	type DecoratorResultSetStateProps,
	useDecorateWithResultAndAPISetState,
} from './use-decorate-with-result-and-api-set-state';
import { ExperiencePerformanceTypes, ExperienceTypes } from '@atlassian/ufo';
import { COMPONENT } from '../../common/ufo-experiences/ufo-experiences';
import { UFOExperienceOneTime } from '../../common/ufo-experiences/ufo-experience-one-time';

/**
 * This decorator adds capabilities of API state tracking and caching to the plain PreQuerySupplier.
 * Think of this as an internal private method of useProductStateMachine.
 *
 * @returns A cached implementation of PreQuery Supplier which is also aware of setting its respective API state.
 */
export const usePreQueryDecorator: ({
	preQueryItemSupplier,
	setAPIState,
	setState,
	id,
}: Pick<ResultSuppliers, 'preQueryItemSupplier'> &
	Pick<DecoratorResultSetStateProps<{}>, 'setState' | 'setAPIState'> & {
		id: string;
	}) => (arg: any) => Promise<SearchItems> = ({
	preQueryItemSupplier,
	setAPIState,
	setState,
	id,
}: Pick<ResultSuppliers, 'preQueryItemSupplier'> &
	Pick<DecoratorResultSetStateProps<{}>, 'setState' | 'setAPIState'> & {
		id: string;
	}) => {
	const [preQueryCache] = useState<SimpleCache<Promise<SearchItems>, [PreQuerySupplierArgs]>>(
		new SimpleCache(preQueryItemSupplier),
	);

	const searchItemSupplier = useCallback(
		(args: any) => preQueryCache.getAsync(args).value,
		[preQueryCache],
	);

	const ufoPreQueryLoadExperience = useRef<UFOExperienceOneTime>(
		new UFOExperienceOneTime(`${id}-pre-query-results-load`, {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
			platform: { component: COMPONENT },
		}),
	);

	return useDecorateWithResultAndAPISetState({
		id,
		setState,
		setAPIState,
		searchItemSupplier,
		ufoExperience: ufoPreQueryLoadExperience.current,
	});
};
